import { useState } from 'react';
import { useT } from '@/store';
import { VehicleGrid } from '@/components/fleet/VehicleGrid';
import { DefectKanban } from '@/components/fleet/DefectKanban';
import { ServicePanel } from '@/components/fleet/ServicePanel';
import { useServiceDue, useServiceOnTime } from '@/hooks/useAnalytics';
import { SmartImportModal } from '@/components/data/SmartImportModal';
import { ServiceFormModal } from '@/components/fleet/ServiceFormModal';
import { ServicePlanList } from '@/components/fleet/ServicePlanList';
import { ServiceHistoryPanel } from '@/components/fleet/ServiceHistoryPanel';

export function FleetView() {
  const t = useT();
  const due = useServiceDue();
  const onTime = useServiceOnTime();
  // Every deadline in the panel below is measured from the last recorded
  // service, so a fleet with no history reads "no baseline" on every row.
  // Typing that in is 20 trucks x 3 jobs of form-filling on install day; the
  // carrier already has it in a spreadsheet.
  const [importingHistory, setImportingHistory] = useState(false);
  // Two verbs, one modal. Until this landed the nine service routes could
  // only be reached through the API, so the checklist told a new carrier to
  // set up a schedule and gave him nowhere to do it — the done-for-you cost
  // this whole audit is about removing.
  const [serviceForm, setServiceForm] = useState(null);
  return (
    <div className="space-y-4">
      <VehicleGrid />
      {/* Above the defect board on purpose. A defect is a truck that has
          already broken; this panel is the fortnight before that, and the
          order the two are read in is the whole argument for having it. */}
      <div className="flex flex-wrap justify-end gap-2">
        {/* Bulk first, single second: on install day the spreadsheet is the
            job, and afterwards it is one workshop visit at a time. */}
        <button type="button" className="btn" onClick={() => setImportingHistory(true)}>
          {t('import.titleServiceRecords')}
        </button>
        <button type="button" className="btn" onClick={() => setServiceForm('record')}>
          {t('serviceForm.titleRecord')}
        </button>
        <button type="button" className="btn btn-primary" onClick={() => setServiceForm('plan')}>
          {t('serviceForm.titlePlan')}
        </button>
      </div>
      {serviceForm ? (
        <ServiceFormModal mode={serviceForm} onClose={() => setServiceForm(null)} />
      ) : null}
      <SmartImportModal
        open={importingHistory}
        onClose={() => setImportingHistory(false)}
        entityType="service_record"
        title={t('import.titleServiceRecords')}
      />
      <ServicePanel
        due={due.data}
        onTime={onTime.data}
        isLoading={due.isLoading || onTime.isLoading}
        isError={due.isError || onTime.isError}
        error={due.error || onTime.error}
      />
      {/* Under the deadlines it produces, not above them. The panel is what a
          dispatcher opens this tab for; the rules are what he comes to when
          one of those rows is wrong — including when the route has just told
          him to go and edit a plan he already has. */}
      <ServicePlanList />
      {/* Under the rules, and last of the three service panels, because that
          is the order a carrier arrives at them in: the deadlines are what
          he opened the tab for, the rules are what he checks when one looks
          wrong, and the history is where the answer usually is — a service
          typed against the wrong truck, or an odometer one digit out. Until
          V-60 the trail stopped at the rules, with the routes to read and
          correct a record tested and unreachable since V-11. */}
      <ServiceHistoryPanel />
      <DefectKanban />
    </div>
  );
}
